import { useState, useRef, useCallback } from 'react';
import { API_BASE } from '../services/apiClient';

/**
 * Hook for self-help chatbot SSE streaming with progressive rendering.
 *
 * Key design: after processing each chunk from reader.read() that contains
 * text_delta events, we flush state and yield to the macrotask queue via
 * setTimeout(0). This is necessary because reader.read() resolves as a
 * microtask — without an explicit yield, the while loop chains microtasks
 * back-to-back and React never gets a chance to render until the stream
 * is fully consumed (producing a "blob" instead of progressive text).
 */
export function useSelfHelpChat({ getToken }) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamingCitations, setStreamingCitations] = useState([]);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const textBuffer = useRef('');
  const citationsRef = useRef([]);
  const lastMetricsRef = useRef(null);

  const sendMessage = useCallback(
    async (sessionId, content, context) => {
      setIsStreaming(true);
      setStreamingText('');
      setStreamingCitations([]);
      setError(null);
      textBuffer.current = '';
      citationsRef.current = [];

      // Performance timing
      const t0 = performance.now();
      let ttfb = null;
      let ttft = null;
      let tokenCount = 0;
      let retrievalMs = null;
      let generationMs = null;

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = typeof getToken === 'function' ? await getToken() : null;
        const url = `${API_BASE}/api/chatbot/sessions/${sessionId}/messages`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ content, context }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `HTTP ${response.status}`);
        }

        // TTFB: time to first byte from SSE connection
        ttfb = performance.now() - t0;

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let messageId = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          let hadTextDelta = false;

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6);
            if (!jsonStr) continue;

            try {
              const event = JSON.parse(jsonStr);

              if (event.type === 'text_delta' && event.text) {
                // TTFT: time to first token
                if (ttft === null) ttft = performance.now() - t0;
                tokenCount++;
                textBuffer.current += event.text;
                hadTextDelta = true;
              } else if (event.type === 'citations' && event.sources) {
                citationsRef.current = event.sources;
                setStreamingCitations(event.sources);
              } else if (event.type === 'timing') {
                if (event.phase === 'retrieval') retrievalMs = event.ms;
              } else if (event.type === 'done') {
                messageId = event.messageId;
                if (event.timing) {
                  retrievalMs = event.timing.retrievalMs ?? retrievalMs;
                  generationMs = event.timing.generationMs ?? null;
                }
              } else if (event.type === 'error') {
                setError(event.error);
              }
            } catch {
              // Ignore malformed SSE events
            }
          }

          // Flush text to state and yield to macrotask queue so React can render.
          // Without this yield, the while loop chains microtasks (reader.read()
          // resolves as a microtask) and React never re-renders until stream ends.
          if (hadTextDelta) {
            setStreamingText(textBuffer.current);
            await new Promise((r) => setTimeout(r, 0));
          }
        }

        // Final flush
        setStreamingText(textBuffer.current);

        // Capture performance metrics
        const totalTime = performance.now() - t0;
        const metrics = {
          ttfb,
          ttft,
          totalTime,
          tokenCount,
          tokensPerSecond: tokenCount > 0 ? tokenCount / (totalTime / 1000) : 0,
          retrievalMs,
          generationMs,
        };
        lastMetricsRef.current = metrics;

        // Expose for E2E tests and dev debugging
        if (typeof window !== 'undefined') {
          window.__CHATBOT_PERF__ = metrics;
          if (process.env.NODE_ENV === 'development') {
            console.debug('[chatbot-perf]', metrics);
          }
        }

        return { text: textBuffer.current, messageId, citations: citationsRef.current };
      } catch (err) {
        if (err.name === 'AbortError') {
          // User cancelled — not an error
          return null;
        }
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setError(msg);
        return null;
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [getToken],
  );

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  return {
    isStreaming,
    streamingText,
    streamingCitations,
    error,
    sendMessage,
    cancelStream,
    lastMetrics: lastMetricsRef,
  };
}
